<template>
    <section class="component two-factor">
        <section class="component__header">
            <span class="component__header__title">
                {{ $t('com.file.profile.two-factor-title') }}
            </span>
        </section>

        <section class="component__body two-factor__body">
            <WarningBox v-if="errorKey">
                {{ $t(errorKey) }}
            </WarningBox>

            <p v-if="successKey" class="two-factor__success">
                {{ $t(successKey) }}
            </p>

            <template v-if="action === null">
                <section class="two-factor__status">
                    <span class="two-factor__label">{{ $t('com.file.profile.two-factor-status') }}:</span>
                    <span :class="settings.isEnabled ? 'two-factor__enabled' : 'two-factor__disabled'">
                        {{ settings.isEnabled ? $t('com.file.profile.two-factor-enabled') : $t('com.file.profile.two-factor-disabled') }}
                    </span>
                </section>

                <p class="two-factor__description">
                    {{ $t('com.file.profile.two-factor-description') }}
                </p>

                <p v-if="settings.requiredByTenant" class="two-factor__notice">
                    {{ $t('com.file.profile.two-factor-required-by-organization') }}
                </p>
                <p v-else-if="settings.requiredByUser" class="two-factor__notice">
                    {{ $t('com.file.contacts.two-factor-required-by-user') }}
                </p>
                <p v-if="settings.requiresReenrollment" class="two-factor__notice">
                    {{ $t('com.file.contacts.two-factor-reenrollment-info') }}
                </p>

                <section v-if="settings.isEnabled" class="two-factor__status">
                    <span class="two-factor__label">{{ $t('com.file.profile.two-factor-recovery-codes-left') }}:</span>
                    <span>{{ settings.remainingRecoveryCodes }}</span>
                </section>

                <p v-if="!settings.isEnabled && !settings.enrollmentEnabled && !isRequired"
                    class="two-factor__notice">
                    {{ $t('com.file.profile.two-factor-enrollment-disabled') }}
                </p>

                <section class="two-factor__actions">
                    <button v-if="!settings.isEnabled" type="button" class="component__button component__button--small"
                        :disabled="!settings.enrollmentEnabled && !isRequired" @click="openEnrollment">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-configure') }}</span>
                    </button>
                    <template v-else>
                        <button type="button" class="component__button component__button--small component__button--white"
                            @click="openRegenerateRecoveryCodes">
                            <span class="component__button__text">{{ $t('com.file.profile.two-factor-regenerate-codes') }}</span>
                        </button>
                        <button v-if="!isRequired" type="button"
                            class="component__button component__button--small" @click="openDisable">
                            <span class="component__button__text">{{ $t('com.file.profile.two-factor-disable') }}</span>
                        </button>
                    </template>
                </section>
            </template>

            <form v-else-if="action === 'enrollmentPassword'" class="two-factor__form" @submit.prevent="startEnrollment">
                <p>{{ $t('com.file.profile.two-factor-password-confirmation-info') }}</p>
                <label for="two-factor-current-password" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-current-password') }}
                </label>
                <input id="two-factor-current-password" v-model="currentPassword" class="two-factor__input"
                    type="password" autocomplete="current-password" />
                <section class="two-factor__actions">
                    <button type="button" class="component__button component__button--small component__button--white" @click="resetAction">
                        <span class="component__button__text">{{ $t('com.global.cancel') }}</span>
                    </button>
                    <button type="submit" class="component__button component__button--small">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-next') }}</span>
                    </button>
                </section>
            </form>

            <form v-else-if="action === 'enrollmentVerification'" class="two-factor__form" @submit.prevent="confirmEnrollment">
                <p>{{ $t('com.file.profile.two-factor-scan-info') }}</p>
                <section class="two-factor__enrollment">
                    <img v-if="qrCodeDataUrl" class="two-factor__qr" :src="qrCodeDataUrl"
                        :alt="$t('com.file.profile.two-factor-qr-alt')" />
                    <section class="two-factor__manual-key">
                        <span class="two-factor__label">{{ $t('com.file.profile.two-factor-manual-key') }}</span>
                        <code>{{ enrollment.manualKey }}</code>
                        <span v-if="enrollment.accountName">{{ $t('com.file.profile.two-factor-account') }}: {{ enrollment.accountName }}</span>
                        <span v-if="enrollment.issuer">{{ $t('com.file.profile.two-factor-issuer') }}: {{ enrollment.issuer }}</span>
                    </section>
                </section>
                <p v-if="enrollment.expiresInSeconds" class="two-factor__notice">
                    {{ $t('com.file.profile.two-factor-enrollment-expires', { seconds: enrollment.expiresInSeconds }) }}
                </p>
                <label for="two-factor-confirm-code" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-code') }}
                </label>
                <input id="two-factor-confirm-code" v-model.trim="verificationCode" class="two-factor__input"
                    inputmode="numeric" autocomplete="one-time-code" maxlength="6" />
                <section class="two-factor__actions">
                    <button type="button" class="component__button component__button--small component__button--white" @click="resetAction">
                        <span class="component__button__text">{{ $t('com.global.cancel') }}</span>
                    </button>
                    <button type="submit" class="component__button component__button--small">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-activate') }}</span>
                    </button>
                </section>
            </form>

            <section v-else-if="action === 'recoveryCodes'" class="two-factor__form">
                <h3>{{ $t('com.file.profile.two-factor-recovery-codes-title') }}</h3>
                <p class="two-factor__notice">{{ $t('com.file.profile.two-factor-recovery-codes-once') }}</p>
                <section class="two-factor__codes">
                    <code v-for="recoveryCode in recoveryCodes" :key="recoveryCode">{{ recoveryCode }}</code>
                </section>
                <section class="two-factor__actions">
                    <button type="button" class="component__button component__button--small component__button--white" @click="copyRecoveryCodes">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-copy-codes') }}</span>
                    </button>
                    <button type="button" class="component__button component__button--small component__button--white" @click="downloadRecoveryCodes">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-download-codes') }}</span>
                    </button>
                </section>
                <label class="two-factor__confirmation">
                    <input v-model="recoveryCodesSaved" type="checkbox" />
                    <span>{{ $t('com.file.profile.two-factor-codes-saved-confirmation') }}</span>
                </label>
                <button type="button" class="component__button component__button--small" :disabled="!recoveryCodesSaved"
                    @click="finishRecoveryCodes">
                    <span class="component__button__text">{{ $t('com.file.profile.two-factor-finish') }}</span>
                </button>
            </section>

            <form v-else-if="action === 'regenerateRecoveryCodes'" class="two-factor__form" @submit.prevent="regenerateRecoveryCodes">
                <p>{{ $t('com.file.profile.two-factor-regenerate-info') }}</p>
                <label for="two-factor-regenerate-password" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-current-password') }}
                </label>
                <input id="two-factor-regenerate-password" v-model="currentPassword" class="two-factor__input"
                    type="password" autocomplete="current-password" />
                <label for="two-factor-regenerate-code" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-code') }}
                </label>
                <input id="two-factor-regenerate-code" v-model.trim="verificationCode" class="two-factor__input"
                    inputmode="numeric" autocomplete="one-time-code" maxlength="6" />
                <section class="two-factor__actions">
                    <button type="button" class="component__button component__button--small component__button--white" @click="resetAction">
                        <span class="component__button__text">{{ $t('com.global.cancel') }}</span>
                    </button>
                    <button type="submit" class="component__button component__button--small">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-regenerate-codes') }}</span>
                    </button>
                </section>
            </form>

            <form v-else-if="action === 'disable'" class="two-factor__form" @submit.prevent="disableTwoFactor">
                <p class="two-factor__notice">{{ $t('com.file.profile.two-factor-disable-warning') }}</p>
                <label for="two-factor-disable-password" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-current-password') }}
                </label>
                <input id="two-factor-disable-password" v-model="currentPassword" class="two-factor__input"
                    type="password" autocomplete="current-password" />
                <label for="two-factor-disable-method" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-confirmation-method') }}
                </label>
                <select id="two-factor-disable-method" v-model="verificationMethod" class="two-factor__input">
                    <option value="totp">{{ $t('com.file.profile.two-factor-method-totp') }}</option>
                    <option value="recovery_code">{{ $t('com.file.profile.two-factor-method-recovery-code') }}</option>
                </select>
                <label for="two-factor-disable-code" class="two-factor__label">
                    {{ $t('com.file.profile.two-factor-code') }}
                </label>
                <input id="two-factor-disable-code" v-model.trim="verificationCode" class="two-factor__input"
                    :inputmode="verificationMethod === 'totp' ? 'numeric' : 'text'" autocomplete="one-time-code" />
                <section class="two-factor__actions">
                    <button type="button" class="component__button component__button--small component__button--white" @click="resetAction">
                        <span class="component__button__text">{{ $t('com.global.cancel') }}</span>
                    </button>
                    <button type="submit" class="component__button component__button--small">
                        <span class="component__button__text">{{ $t('com.file.profile.two-factor-disable') }}</span>
                    </button>
                </section>
            </form>
        </section>
    </section>
</template>

<script charset="utf-8">
import QRCode from "qrcode";
import WarningBox from "@/components/BasicControls/WarningBox.vue";
import UserService from "@/services/user.service";

export default {
    name: "TwoFactorSecurity",
    components: {
        WarningBox,
    },
    data() {
        return {
            settings: {
                isEnabled: false,
                enrollmentEnabled: false,
                remainingRecoveryCodes: 0,
                requiredByTenant: false,
                requiredByUser: false,
                requiresReenrollment: false,
                isRequired: false,
            },
            action: null,
            currentPassword: "",
            verificationCode: "",
            verificationMethod: "totp",
            enrollment: {
                provisioningUri: "",
                manualKey: "",
                issuer: "",
                accountName: "",
                expiresInSeconds: 0,
            },
            qrCodeDataUrl: "",
            recoveryCodes: [],
            recoveryCodesSaved: false,
            errorKey: null,
            successKey: null,
        };
    },
    async mounted() {
        await this.fetchSettings();
    },
    computed: {
        isRequired() {
            return this.settings.isRequired || this.settings.requiredByTenant
                || this.settings.requiredByUser || this.settings.requiresReenrollment;
        },
    },
    methods: {
        async fetchSettings() {
            try {
                const response = await UserService.getTwoFactorSettings();
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const data = response.data ?? {};
                this.settings = {
                    isEnabled: Boolean(data.isEnabled ?? data.enabled ?? data.totpEnabled),
                    enrollmentEnabled: data.enrollmentEnabled !== false,
                    remainingRecoveryCodes: Number(data.remainingRecoveryCodes ?? data.recoveryCodesLeft ?? 0),
                    requiredByTenant: Boolean(data.requiredByTenant),
                    requiredByUser: Boolean(data.requiredByUser),
                    requiresReenrollment: Boolean(data.requiresReenrollment),
                    isRequired: Boolean(data.isRequired),
                };
            }
            catch {
                this.errorKey = "com.file.profile.two-factor-settings-error";
            }
        },
        openEnrollment() {
            if (!this.settings.enrollmentEnabled && !this.isRequired) {
                return;
            }

            this.clearMessages();
            this.action = "enrollmentPassword";
        },
        async startEnrollment() {
            if (!this.currentPassword) {
                this.errorKey = "com.file.profile.two-factor-password-required";
                return;
            }

            try {
                this.clearMessages();
                const response = await UserService.startTotpEnrollment(this.currentPassword);
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const data = response.data ?? {};
                if (!data.provisioningUri || !data.manualKey) {
                    this.errorKey = "com.file.profile.two-factor-configuration-error";
                    return;
                }

                this.enrollment = {
                    provisioningUri: data.provisioningUri,
                    manualKey: data.manualKey,
                    issuer: data.issuer ?? "eSesja",
                    accountName: data.accountName ?? "",
                    expiresInSeconds: Number(data.expiresInSeconds ?? 0),
                };
                this.qrCodeDataUrl = await QRCode.toDataURL(this.enrollment.provisioningUri, {
                    errorCorrectionLevel: "M",
                    margin: 1,
                    width: 220,
                });
                this.currentPassword = "";
                this.action = "enrollmentVerification";
            }
            catch {
                this.errorKey = "com.file.profile.two-factor-configuration-error";
            }
        },
        async confirmEnrollment() {
            if (!this.verificationCode) {
                this.errorKey = "com.file.profile.two-factor-code-required";
                return;
            }

            try {
                this.clearMessages();
                const response = await UserService.confirmTotpEnrollment(this.verificationCode);
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const recoveryCodes = this.extractRecoveryCodes(response.data);
                this.verificationCode = "";
                this.clearEnrollmentData();
                await this.fetchSettings();

                if (recoveryCodes.length === 0) {
                    this.action = null;
                    this.errorKey = "com.file.profile.two-factor-recovery-codes-error";
                    return;
                }

                this.showRecoveryCodes(recoveryCodes);
            }
            catch {
                this.errorKey = "com.file.profile.two-factor-invalid-code";
            }
        },
        openRegenerateRecoveryCodes() {
            this.clearMessages();
            this.action = "regenerateRecoveryCodes";
        },
        async regenerateRecoveryCodes() {
            if (!this.currentPassword || !this.verificationCode) {
                this.errorKey = "com.file.profile.two-factor-fields-required";
                return;
            }

            try {
                this.clearMessages();
                const response = await UserService.regenerateTwoFactorRecoveryCodes(this.currentPassword, this.verificationCode);
                if (response.status !== 200) {
                    throw await Promise.reject(response);
                }

                const recoveryCodes = this.extractRecoveryCodes(response.data);
                if (recoveryCodes.length === 0) {
                    this.errorKey = "com.file.profile.two-factor-recovery-codes-error";
                    return;
                }

                this.currentPassword = "";
                this.verificationCode = "";
                this.showRecoveryCodes(recoveryCodes);
            }
            catch {
                this.errorKey = "com.file.profile.two-factor-regenerate-error";
            }
        },
        openDisable() {
            if (this.isRequired) {
                return;
            }

            this.clearMessages();
            this.action = "disable";
            this.verificationMethod = "totp";
        },
        async disableTwoFactor() {
            if (this.isRequired) {
                this.resetAction();
                return;
            }

            if (!this.currentPassword || !this.verificationCode) {
                this.errorKey = "com.file.profile.two-factor-fields-required";
                return;
            }

            try {
                this.clearMessages();
                const response = await UserService.disableTwoFactor(this.currentPassword, this.verificationCode, this.verificationMethod);
                if (response.status !== 200 && response.status !== 204) {
                    throw await Promise.reject(response);
                }

                this.resetAction();
                this.successKey = "com.file.profile.two-factor-disabled-success";
                await this.fetchSettings();
            }
            catch {
                this.errorKey = "com.file.profile.two-factor-disable-error";
            }
        },
        extractRecoveryCodes(data) {
            if (Array.isArray(data)) {
                return data;
            }

            const recoveryCodes = data?.recoveryCodes ?? data?.codes ?? [];
            return Array.isArray(recoveryCodes) ? recoveryCodes : [];
        },
        showRecoveryCodes(recoveryCodes) {
            this.recoveryCodes = recoveryCodes;
            this.recoveryCodesSaved = false;
            this.action = "recoveryCodes";
        },
        async copyRecoveryCodes() {
            try {
                await navigator.clipboard.writeText(this.recoveryCodes.join("\n"));
                this.successKey = "com.file.profile.two-factor-codes-copied";
            }
            catch {
                this.errorKey = "com.file.profile.two-factor-copy-error";
            }
        },
        downloadRecoveryCodes() {
            const blob = new Blob([this.recoveryCodes.join("\r\n")], { type: "text/plain;charset=utf-8" });
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = "esesja-kody-awaryjne.txt";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
        },
        async finishRecoveryCodes() {
            if (!this.recoveryCodesSaved) {
                return;
            }

            this.recoveryCodes = [];
            this.recoveryCodesSaved = false;
            this.action = null;
            this.successKey = "com.file.profile.two-factor-enabled-success";
            await this.fetchSettings();
        },
        resetAction() {
            this.action = null;
            this.currentPassword = "";
            this.verificationCode = "";
            this.verificationMethod = "totp";
            this.clearEnrollmentData();
            this.recoveryCodes = [];
            this.recoveryCodesSaved = false;
            this.clearMessages();
        },
        clearEnrollmentData() {
            this.enrollment = {
                provisioningUri: "",
                manualKey: "",
                issuer: "",
                accountName: "",
                expiresInSeconds: 0,
            };
            this.qrCodeDataUrl = "";
        },
        clearMessages() {
            this.errorKey = null;
            this.successKey = null;
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/user-profile-component.scss";

.two-factor {
    flex: 1 1 600px;
    height: fit-content;

    &__body,
    &__form {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    &__body {
        min-height: 0;
        padding: 20px;
    }

    &__form p,
    &__description {
        margin: 0;
    }

    &__status {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
    }

    &__label {
        font-weight: 700;
    }

    &__enabled {
        color: #157347;
        font-weight: 700;
    }

    &__disabled {
        color: #d80418;
        font-weight: 700;
    }

    &__success {
        margin: 0;
        padding: 12px;
        color: #0f5132;
        background: #d1e7dd;
        border: 1px solid #badbcc;
        border-radius: 4px;
    }

    &__notice {
        padding: 12px;
        color: #664d03;
        background: #fff3cd;
        border: 1px solid #ffecb5;
        border-radius: 4px;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }

    &__input {
        width: 100%;
        padding: 10px 12px;
        background: #ffffff;
        border: 1px solid #bbc2ce;
        border-radius: 4px;
        box-sizing: border-box;
    }

    &__enrollment {
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
        align-items: center;
    }

    &__qr {
        width: 220px;
        height: 220px;
        background: #ffffff;
        border: 1px solid #dce4ee;
    }

    &__manual-key {
        display: flex;
        flex: 1 1 240px;
        flex-direction: column;
        gap: 8px;
        min-width: 0;

        code {
            overflow-wrap: anywhere;
            padding: 12px;
            font-size: 16px;
            background: #f4f6f9;
            border-radius: 4px;
        }
    }

    &__codes {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 8px;

        code {
            padding: 10px;
            text-align: center;
            background: #f4f6f9;
            border-radius: 4px;
        }
    }

    &__confirmation {
        display: flex;
        gap: 10px;
        align-items: flex-start;
    }

    button:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
}
</style>
